/**
 * EditorialProgressMap (iter75)
 *
 * The Rally Lab progress map — a professional, editorial-style curved
 * timeline replacing the retired Helter Skelter. Same data source
 * (`/api/students/{id}/pathway`) so the coach's ticks flow through
 * without any migration. Theme-aware, click-through to
 * `/offerings?focus=<axis>`, footnote reminds students that mastery is
 * level-relative.
 *
 * Visual:
 *   · Curved path traced across a subtle court-lined background.
 *   · Five numbered markers along the curve — filled = mastered,
 *     ringed = in-progress, hollow = not-started.
 *   · Each marker has a zone label + a level pill + a subtle progress
 *     meter showing X/Y competent ticks for that zone.
 *   · Footnote below the SVG explains what "mastered" actually means.
 */
import { useEffect, useMemo, useState } from "react";
import { Loader, Info, Printer } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import "./EditorialProgressMap.css";

// 5 evenly-spaced points along a hand-tuned Bézier curve inside the
// 1000 × 500 viewBox. Nudged so the labels below each marker never overlap.
const MARKER_POSITIONS = [
  { x: 130, y: 380 },
  { x: 320, y: 260 },
  { x: 500, y: 210 },
  { x: 700, y: 240 },
  { x: 900, y: 140 },
];

const FOOTNOTE =
  "Progress shown is relative to your target level's expectations. " +
  "There's always more to master — new skills open up as you level up.";

function masteryStyle(state) {
  switch (state) {
    case "mastered":   return { fill: "var(--teal)", stroke: "var(--teal)", label: "MASTERED" };
    case "in_progress":return { fill: "var(--surface)", stroke: "var(--teal)", label: "IN PROGRESS" };
    default:           return { fill: "var(--surface)", stroke: "var(--border-strong)", label: "TO GO" };
  }
}

export default function EditorialProgressMap({
  studentId,
  sport = "pickleball",
  showPrintButton = true,
  printMode = false,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tipOpen, setTipOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!studentId) return;
    let cancelled = false;
    setLoading(true); setError(null);
    api.get(`/students/${studentId}/pathway`, { params: { sport } })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.detail || "Failed to load pathway"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [studentId, sport]);

  const zones = useMemo(() => (data?.zones || []).slice(0, 5), [data]);
  const masteredCount = useMemo(() => zones.filter((z) => z.mastery === "mastered").length, [zones]);

  const handleZoneClick = (zone) => {
    if (printMode || !zone) return;
    const focus = zone.axis || zone.label;
    navigate(`/offerings?focus=${encodeURIComponent(focus)}`);
  };

  if (loading) {
    return (
      <div className="editorial-map editorial-map--loading" data-testid="editorial-map-loading">
        <Loader className="w-6 h-6 animate-spin" style={{ color: "var(--teal)" }} />
        <p>Loading your progress map…</p>
      </div>
    );
  }
  if (error) {
    return (
      <div className="editorial-map editorial-map--error" data-testid="editorial-map-error">
        <p>{error}</p>
      </div>
    );
  }
  if (!data || zones.length === 0) return null;

  return (
    <div
      className={printMode ? "editorial-map editorial-map--print" : "editorial-map"}
      data-testid="editorial-progress-map"
    >
      {/* Header */}
      {!printMode && (
        <div className="editorial-map__header">
          <div>
            <div className="editorial-map__eyebrow">YOUR PICKLEBALL PATHWAY</div>
            <h2 className="editorial-map__title">
              {data.target_level
                ? `Progress toward Level ${data.target_level}`
                : "Progress toward your next level"}
            </h2>
            <p className="editorial-map__subtitle">
              {masteredCount} of {zones.length} zones mastered so far · tap a marker to book a class that works on it.
            </p>
          </div>
          {showPrintButton && (
            <Link
              to={`/pathway/print/${studentId}?sport=${sport}`}
              target="_blank"
              data-testid="pathway-print-link"
              className="editorial-map__print-btn"
            >
              <Printer className="w-3 h-3" /> Print pathway
            </Link>
          )}
        </div>
      )}

      {/* SVG timeline */}
      <div className="editorial-map__canvas">
        <MapSVG zones={zones} onZoneClick={handleZoneClick} printMode={printMode} />
      </div>

      {/* Zone summary cards below the timeline — mirrors the SVG order */}
      <div className="editorial-map__grid" data-testid="editorial-zone-list">
        {zones.map((z, i) => (
          <ZoneSummary key={z.key} zone={z} index={i + 1} printMode={printMode} onClick={handleZoneClick} />
        ))}
      </div>

      {/* Footnote — level-relative disclaimer */}
      <div className="editorial-map__footnote" data-testid="editorial-map-footnote">
        <button
          type="button"
          className="editorial-map__info-btn"
          data-testid="editorial-map-info"
          onClick={() => setTipOpen((v) => !v)}
          aria-label="What does 'mastered' mean?"
        >
          <Info className="w-3 h-3" />
          <span>What does <em>mastered</em> mean?</span>
        </button>
        <p className={tipOpen ? "editorial-map__footnote-copy is-open" : "editorial-map__footnote-copy"}>
          {FOOTNOTE}
        </p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SVG scene — curved editorial path with 5 numbered markers.
// ---------------------------------------------------------------------------
function MapSVG({ zones, onZoneClick, printMode }) {
  const W = 1000, H = 500;
  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className="editorial-map__svg"
      role="img"
      aria-label="Pickleball progress map"
      data-testid="editorial-map-svg"
    >
      <defs>
        <linearGradient id="edMapBg" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="var(--surface)" stopOpacity="0.9" />
          <stop offset="1" stopColor="var(--surface-hover)" stopOpacity="0.4" />
        </linearGradient>
        <pattern id="edCourtGrid" width="80" height="80" patternUnits="userSpaceOnUse">
          <path d="M 80 0 L 0 0 0 80" fill="none" stroke="var(--border)" strokeWidth="0.6" opacity="0.6" />
        </pattern>
        <filter id="edSoft" x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="2" stdDeviation="3" floodOpacity="0.12" />
        </filter>
      </defs>

      {/* Background */}
      <rect x="0" y="0" width={W} height={H} fill="url(#edMapBg)" />
      <rect x="0" y="0" width={W} height={H} fill="url(#edCourtGrid)" />

      {/* Curved path — trailing dashed portion for the remaining stretch */}
      <path
        d="M 90 420 C 200 380, 260 250, 350 240 S 480 210, 520 210 S 660 260, 720 220 S 830 130, 940 130"
        fill="none"
        stroke="var(--border-strong)"
        strokeWidth="3"
        strokeLinecap="round"
        strokeDasharray="6 8"
        opacity="0.55"
      />
      {/* Solid path segment — up to the last mastered marker */}
      <SolidPathToProgress zones={zones} />

      {/* Markers */}
      {zones.map((z, i) => {
        const pos = MARKER_POSITIONS[i] || { x: 900, y: 100 + i * 100 };
        return (
          <Marker key={z.key} pos={pos} zone={z} index={i + 1}
                  onClick={onZoneClick} printMode={printMode} />
        );
      })}
    </svg>
  );
}

// Draws a solid segment of the path only up to the last mastered marker,
// giving the visual "completed vs remaining" storytelling without a full
// stroke-dashoffset animation (which would need runtime measurement).
function SolidPathToProgress({ zones }) {
  const lastMasteredIdx = [...zones].reverse().findIndex((z) => z.mastery === "mastered");
  if (lastMasteredIdx === -1) return null;
  const idx = zones.length - 1 - lastMasteredIdx;
  const target = MARKER_POSITIONS[idx];
  if (!target) return null;
  // We approximate the segment by trimming with a clipPath box.
  const clipW = target.x + 10;
  return (
    <g>
      <clipPath id="edPathClip">
        <rect x="0" y="0" width={clipW} height={500} />
      </clipPath>
      <path
        d="M 90 420 C 200 380, 260 250, 350 240 S 480 210, 520 210 S 660 260, 720 220 S 830 130, 940 130"
        fill="none"
        stroke="var(--teal)"
        strokeWidth="3.5"
        strokeLinecap="round"
        clipPath="url(#edPathClip)"
      />
    </g>
  );
}

function Marker({ pos, zone, index, onClick, printMode }) {
  const { fill, stroke } = masteryStyle(zone.mastery);
  const solid = zone.mastery === "mastered";
  const clickable = !printMode && typeof onClick === "function";
  const label = zone.label === "Third Shot" ? "3rd Shot" : zone.label;
  return (
    <g
      transform={`translate(${pos.x}, ${pos.y})`}
      data-testid={`pathway-marker-${zone.key}`}
      onClick={clickable ? () => onClick(zone) : undefined}
      style={clickable ? { cursor: "pointer" } : undefined}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === "Enter" || e.key === " ") onClick(zone); } : undefined}
    >
      {/* Halo on mastered markers */}
      {solid && (
        <circle r="30" fill="var(--teal)" opacity="0.15">
          {!printMode && <animate attributeName="r" values="30;36;30" dur="2.8s" repeatCount="indefinite" />}
        </circle>
      )}
      <circle r="22" fill={fill} stroke={stroke} strokeWidth="3" filter="url(#edSoft)" />
      {solid ? (
        <path d="M -8 0 L -2 6 L 9 -6" fill="none" stroke="#0A0A0A" strokeWidth="3"
              strokeLinecap="round" strokeLinejoin="round" />
      ) : (
        <text y="6" fontSize="14" fontWeight="800" textAnchor="middle"
              fill={zone.mastery === "in_progress" ? "var(--teal)" : "var(--muted)"}>
          {index}
        </text>
      )}
      {/* Label below marker */}
      <g transform="translate(0, 44)">
        <text textAnchor="middle" fontSize="12" fontWeight="800"
              fill="var(--text)" style={{ letterSpacing: "0.06em" }}>
          {label.toUpperCase()}
        </text>
        <text y="16" textAnchor="middle" fontSize="10" fontWeight="500"
              fill="var(--muted)" style={{ letterSpacing: "0.04em" }}>
          {zone.axis_level != null ? `Lv ${zone.axis_level}` : "not yet"}
        </text>
      </g>
    </g>
  );
}

// ---------------------------------------------------------------------------
// Compact summary card per zone (right column / grid below the SVG).
// ---------------------------------------------------------------------------
function ZoneSummary({ zone, index, printMode, onClick }) {
  const { label } = masteryStyle(zone.mastery);
  const ticks = zone.ticks || { competent: 0, total: 0 };
  const pct = ticks.total > 0 ? Math.round((ticks.competent / ticks.total) * 100) : 0;
  const clickable = !printMode && typeof onClick === "function";
  return (
    <div
      className={
        "editorial-zone" +
        (zone.mastery === "mastered" ? " editorial-zone--done" : "") +
        (clickable ? " is-clickable" : "")
      }
      data-testid={`pathway-zone-${zone.key}`}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={clickable ? () => onClick(zone) : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === "Enter" || e.key === " ") onClick(zone); } : undefined}
    >
      <div className="editorial-zone__head">
        <span className="editorial-zone__num">{index}</span>
        <span className="editorial-zone__name">{zone.label}</span>
        <span className={"editorial-zone__chip chip--" + zone.mastery}>{label}</span>
      </div>
      {zone.weakest_subskill ? (
        <div className="editorial-zone__focus">
          <div className="editorial-zone__focus-eyebrow">
            Next focus · Level {zone.weakest_subskill.level}
          </div>
          <div className="editorial-zone__focus-name">{zone.weakest_subskill.name}</div>
          <div className="editorial-zone__focus-crit">{zone.weakest_subskill.criteria}</div>
        </div>
      ) : (
        <div className="editorial-zone__empty">
          Not yet mapped — your coach will set this at your next session.
        </div>
      )}
      {ticks.total > 0 && (
        <div className="editorial-zone__meter">
          <div className="editorial-zone__meter-bar">
            <div className="editorial-zone__meter-fill" style={{ width: `${pct}%` }} />
          </div>
          <div className="editorial-zone__meter-lbl">
            {ticks.competent}/{ticks.total} skills for {zone.axis_level != null ? `Lv ${zone.axis_level}` : "this level"}
          </div>
        </div>
      )}
    </div>
  );
}
